import { useEffect, useMemo, useState } from "react";
import api, { formatEUR, formatDate, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wallet, Send, Bell, TrendingUp, AlertCircle } from "lucide-react";

export default function CobrosPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    let pagado = 0;
    let pendiente = 0;
    let vencido = 0;
    let vencidoCount = 0;
    for (const i of items) {
      if (i.status === "pagada") pagado += i.total;
      else if (i.status === "vencida") {
        vencido += i.total;
        vencidoCount += 1;
      } else if (i.status === "emitida") pendiente += i.total;
    }
    return { pagado, pendiente, vencido, vencidoCount };
  }, [items]);

  const overdue = items.filter((i) => i.status === "vencida");
  const pending = items.filter((i) => i.status === "emitida");

  const sendReminder = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/send-reminder`);
      if (data.sent) toast.success("Recordatorio enviado");
      else toast.error("No se pudo enviar (revisa email del cliente)");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="cobros-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
          Cobros y ventas
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Controla lo cobrado, lo pendiente y las facturas vencidas.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5 stat-card">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 grid place-items-center">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="mt-4 text-sm text-slate-500">Cobrado (histórico)</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="cobrado-total">
            {formatEUR(stats.pagado)}
          </div>
        </Card>
        <Card className="p-5 stat-card">
          <div className="w-10 h-10 rounded-lg bg-amber-50 grid place-items-center">
            <Wallet className="w-5 h-5 text-amber-600" />
          </div>
          <div className="mt-4 text-sm text-slate-500">Pendiente de cobro</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="pendiente-total">
            {formatEUR(stats.pendiente)}
          </div>
        </Card>
        <Card className="p-5 stat-card">
          <div className="w-10 h-10 rounded-lg bg-rose-50 grid place-items-center">
            <AlertCircle className="w-5 h-5 text-rose-600" />
          </div>
          <div className="mt-4 text-sm text-slate-500">Vencido</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="vencido-total">
            {formatEUR(stats.vencido)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {stats.vencidoCount} factura{stats.vencidoCount === 1 ? "" : "s"}
          </div>
        </Card>
      </div>

      {/* Vencidas */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-rose-600">
              Requiere atención
            </div>
            <div className="font-display text-lg font-bold text-slate-900">
              Facturas vencidas
            </div>
          </div>
        </div>
        {overdue.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">
            No hay facturas vencidas. ¡Bien hecho!
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Número</th>
                <th className="py-2 pr-4">Cliente</th>
                <th className="py-2 pr-4">Vencía</th>
                <th className="py-2 pr-4 text-right">Total</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {overdue.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-medium text-slate-900">{inv.number}</td>
                  <td className="py-3 pr-4 text-slate-700">{inv.client_snapshot?.name}</td>
                  <td className="py-3 pr-4 text-rose-600">{formatDate(inv.due_date)}</td>
                  <td className="py-3 pr-4 text-right font-medium">{formatEUR(inv.total)}</td>
                  <td className="py-3 pr-2 text-right">
                    <Button
                      size="sm"
                      onClick={() => sendReminder(inv.id)}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid={`reminder-btn-${inv.id}`}
                    >
                      <Bell className="w-3 h-3 mr-1" /> Recordar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Pendientes */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
              En curso
            </div>
            <div className="font-display text-lg font-bold text-slate-900">
              Facturas pendientes
            </div>
          </div>
        </div>
        {pending.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">
            No hay facturas pendientes.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Número</th>
                <th className="py-2 pr-4">Cliente</th>
                <th className="py-2 pr-4">Emisión</th>
                <th className="py-2 pr-4">Vencimiento</th>
                <th className="py-2 pr-4 text-right">Total</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-medium text-slate-900">{inv.number}</td>
                  <td className="py-3 pr-4 text-slate-700">{inv.client_snapshot?.name}</td>
                  <td className="py-3 pr-4 text-slate-600">{formatDate(inv.issue_date)}</td>
                  <td className="py-3 pr-4 text-slate-600">
                    {inv.due_date ? formatDate(inv.due_date) : "-"}
                  </td>
                  <td className="py-3 pr-4 text-right font-medium">{formatEUR(inv.total)}</td>
                  <td className="py-3 pr-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => sendReminder(inv.id)}
                      data-testid={`send-reminder-${inv.id}`}
                    >
                      <Send className="w-3 h-3 mr-1" /> Recordar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
